import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { getLookbook, patchLookbook, createSection, patchSection, deleteSection, addItemToSection, removeItemFromSection, reorderSectionItems } from '@/lib/lookbook-api';
import { fetchSubmissionThumbnailDataUrl } from '@/lib/submissions-api';
import { ArrowLeft, Globe, Image, Trash2, ChevronUp, ChevronDown, Star, X } from 'lucide-react';

export default function HostLookbookPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [thumbCache, setThumbCache] = useState({});

  const load = useCallback(async () => {
    if (!eventId) { setError('No event id.'); setLoading(false); return; }
    setLoading(true);
    try { const body = await getLookbook(eventId); setData(body); setError(null); }
    catch (e) { setError(e?.message || 'Failed to load lookbook.'); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  // Preload thumbs
  const preloadThumb = useCallback(async (subId) => {
    if (thumbCache[subId]) return;
    try { const url = await fetchSubmissionThumbnailDataUrl(subId); if (url) setThumbCache(p => ({ ...p, [subId]: url })); } catch {}
  }, [thumbCache]);

  useEffect(() => {
    if (!data) return;
    for (const it of data.selected_tray || []) preloadThumb(it.id);
    for (const s of data.lookbook?.sections || []) for (const sid of s.item_ids) preloadThumb(sid);
    if (data.lookbook?.hero_submission_id) preloadThumb(data.lookbook.hero_submission_id);
  }, [data, preloadThumb]);

  async function patchMeta(patch) {
    if (!eventId) return;
    setSaving(true);
    try { const res = await patchLookbook(eventId, patch); setData(p => p ? { ...p, lookbook: res.lookbook } : p); }
    catch (e) { window.alert(e?.message || 'Save failed.'); }
    finally { setSaving(false); }
  }
  async function addSec() {
    if (!eventId || !newSectionTitle.trim()) return;
    setSaving(true);
    try { const res = await createSection(eventId, newSectionTitle.trim()); setData(p => p ? { ...p, lookbook: res.lookbook } : p); setNewSectionTitle(''); }
    finally { setSaving(false); }
  }
  async function removeSec(sec) {
    if (!eventId || !window.confirm(`Delete section "${sec.title}"?`)) return;
    const res = await deleteSection(eventId, sec.id);
    setData(p => p ? { ...p, lookbook: res.lookbook } : p);
  }
  async function placeTrayItem(subId, sectionId) {
    if (!eventId) return;
    setSaving(true);
    try { await addItemToSection(eventId, sectionId, subId); load(); }
    finally { setSaving(false); }
  }
  async function unplaceItem(subId, sectionId) {
    if (!eventId) return;
    setSaving(true);
    try { await removeItemFromSection(eventId, sectionId, subId); load(); }
    finally { setSaving(false); }
  }
  async function moveItem(sec, subId, delta) {
    if (!eventId) return;
    const ids = [...sec.item_ids];
    const i = ids.indexOf(subId);
    if (i < 0) return;
    const target = Math.max(0, Math.min(ids.length - 1, i + delta));
    if (target === i) return;
    ids.splice(i, 1); ids.splice(target, 0, subId);
    setData(p => p ? { ...p, lookbook: { ...p.lookbook, sections: p.lookbook.sections.map(s => s.id === sec.id ? { ...s, item_ids: ids } : s) } } : p);
    try { await reorderSectionItems(eventId, sec.id, ids); } catch { load(); }
  }
  async function renameSec(sec, title) {
    if (!eventId) return;
    const res = await patchSection(eventId, sec.id, { title });
    setData(p => p ? { ...p, lookbook: res.lookbook } : p);
  }

  const placedCount = data?.lookbook?.sections?.reduce((n, s) => n + s.item_ids.length, 0) ?? 0;
  const canLaunch = placedCount > 0;
  const isLive = data?.manifest?.state === 'LIVE';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="host-lookbook-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="lookbook-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="lookbook-title">Build Lookbook</h1>
      {data?.event?.name && <p className="text-sm text-[#5B6472] mb-4" data-testid="lookbook-event-name">{data.event.name}</p>}

      {loading ? (
        <div className="flex justify-center py-8" data-testid="lookbook-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="lookbook-error">{error}</div>
      ) : data ? (
        <div className="flex flex-col gap-4">
          {isLive && (
            <div className="bg-[#E4F4EC] border border-[#1F8A55]/20 rounded-xl p-4 flex items-center gap-3" data-testid="lookbook-live-banner">
              <Globe className="w-5 h-5 text-[#1F8A55]" />
              <span className="flex-1 font-bold text-[#0B1B46]">Lookbook v{data.manifest?.version} is live</span>
              <button onClick={() => navigate(`/host-lookbook-live?event_id=${encodeURIComponent(eventId)}`)} className="text-[#146BFF] font-bold text-sm" data-testid="lookbook-open-live-admin">Manage →</button>
            </div>
          )}

          {/* Meta */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-3" data-testid="lookbook-meta-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Cover copy</p>
            {['title', 'subtitle', 'intro_text', 'closing_text'].map(f => (
              <div key={f}>
                <label className="block text-sm font-semibold text-[#0B1B46] mb-1 capitalize">{f.replace('_', ' ')}</label>
                {f.includes('text') ? (
                  <textarea value={data.lookbook[f] || ''} onChange={e => setData(p => ({ ...p, lookbook: { ...p.lookbook, [f]: e.target.value } }))} onBlur={() => patchMeta({ [f]: data.lookbook[f] })} rows={2}
                    className="w-full px-4 py-2 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white resize-none" data-testid={`lookbook-${f.replace('_', '-')}-input`} />
                ) : (
                  <input value={data.lookbook[f] || ''} onChange={e => setData(p => ({ ...p, lookbook: { ...p.lookbook, [f]: e.target.value } }))} onBlur={() => patchMeta({ [f]: data.lookbook[f] })}
                    className="w-full h-10 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid={`lookbook-${f}-input`} />
                )}
              </div>
            ))}
          </div>

          {/* Hero */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-hero-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Hero image</p>
            {data.lookbook.hero_submission_id && thumbCache[data.lookbook.hero_submission_id] ? (
              <img src={thumbCache[data.lookbook.hero_submission_id]} alt="" className="w-full h-[180px] rounded-xl object-cover" data-testid="lookbook-hero-preview" />
            ) : (
              <div className="w-full h-[120px] rounded-xl bg-[#F3F5F8] border border-dashed border-[#E3E8EF] flex flex-col items-center justify-center" data-testid="lookbook-hero-empty">
                <Image className="w-6 h-6 text-[#5B6472]" /><p className="text-xs text-[#5B6472] mt-1">Pick a hero from placed items</p>
              </div>
            )}
          </div>

          {/* Tray */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-tray-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Selected — not yet placed ({(data.selected_tray || []).length})</p>
            {(data.selected_tray || []).length === 0 ? (
              <p className="text-sm text-[#5B6472]" data-testid="lookbook-tray-empty">Toggle "Add to Lookbook" in the Vault to add them here.</p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {(data.selected_tray || []).map(it => (
                  <div key={it.id} className="w-[120px] flex-shrink-0 bg-white rounded-lg p-1.5 border border-[#E3E8EF]" data-testid={`lookbook-tray-item-${it.id}`}>
                    {thumbCache[it.id] ? <img src={thumbCache[it.id]} alt="" className="w-[104px] h-[78px] rounded-md object-cover" /> : <div className="w-[104px] h-[78px] rounded-md bg-[#F3F5F8]" />}
                    <p className="text-[11px] font-semibold text-[#0B1B46] truncate mt-1">{it.caption || it.contributor_first_name || 'Untitled'}</p>
                    {(data.lookbook?.sections || []).map(sec => (
                      <button key={sec.id} onClick={() => placeTrayItem(it.id, sec.id)}
                        className="block px-1.5 py-0.5 mt-1 rounded text-[11px] font-bold text-[#146BFF] bg-[#EFF5FF] hover:bg-[#D6E4FF] truncate w-full text-left"
                        data-testid={`lookbook-tray-assign-${it.id}-${sec.id}`}>→ {sec.title.slice(0, 14)}</button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sections */}
          {(data.lookbook?.sections || []).slice().sort((a, b) => a.order - b.order).map(sec => (
            <div key={sec.id} className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid={`lookbook-section-${sec.id}`}>
              <div className="flex items-end gap-3 mb-3">
                <input value={sec.title} onChange={e => setData(p => ({ ...p, lookbook: { ...p.lookbook, sections: p.lookbook.sections.map(s => s.id === sec.id ? { ...s, title: e.target.value } : s) } }))}
                  onBlur={() => renameSec(sec, sec.title)}
                  className="flex-1 h-10 px-4 border border-[#E3E8EF] rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid={`lookbook-section-title-${sec.id}`} />
                <button onClick={() => removeSec(sec)} className="w-9 h-9 rounded-md border border-[#E3E8EF] flex items-center justify-center hover:bg-[#FBE5E5]" data-testid={`lookbook-section-delete-${sec.id}`}>
                  <Trash2 className="w-4 h-4 text-[#C93434]" />
                </button>
              </div>
              {sec.item_ids.length === 0 ? (
                <p className="text-sm text-[#5B6472]">Place items from the tray above.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {sec.item_ids.map((subId, i) => (
                    <div key={subId} className="w-[132px]" data-testid={`lookbook-item-${sec.id}-${subId}`}>
                      {thumbCache[subId] ? <img src={thumbCache[subId]} alt="" className="w-[132px] h-[100px] rounded-lg object-cover" /> : <div className="w-[132px] h-[100px] rounded-lg bg-[#F3F5F8]" />}
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => moveItem(sec, subId, -1)} disabled={i === 0} className="w-7 h-7 rounded border border-[#E3E8EF] flex items-center justify-center disabled:opacity-30" data-testid={`lookbook-item-up-${sec.id}-${subId}`}><ChevronUp className="w-3.5 h-3.5 text-[#146BFF]" /></button>
                        <button onClick={() => moveItem(sec, subId, 1)} disabled={i === sec.item_ids.length - 1} className="w-7 h-7 rounded border border-[#E3E8EF] flex items-center justify-center disabled:opacity-30" data-testid={`lookbook-item-down-${sec.id}-${subId}`}><ChevronDown className="w-3.5 h-3.5 text-[#146BFF]" /></button>
                        <button onClick={() => patchMeta({ hero_submission_id: subId })} className="w-7 h-7 rounded border border-[#E3E8EF] flex items-center justify-center" data-testid={`lookbook-item-hero-${sec.id}-${subId}`}><Star className={`w-3 h-3 ${data.lookbook.hero_submission_id === subId ? 'text-[#B86500] fill-[#B86500]' : 'text-[#5B6472]'}`} /></button>
                        <button onClick={() => unplaceItem(subId, sec.id)} className="w-7 h-7 rounded border border-[#E3E8EF] flex items-center justify-center" data-testid={`lookbook-item-remove-${sec.id}-${subId}`}><X className="w-3.5 h-3.5 text-[#C93434]" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Add section */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-add-section-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Add a section</p>
            <div className="flex gap-2">
              <input value={newSectionTitle} onChange={e => setNewSectionTitle(e.target.value)} placeholder="e.g. Ceremony, Reception"
                className="flex-1 h-10 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="lookbook-new-section-input" />
              <button onClick={addSec} disabled={!newSectionTitle.trim()}
                className="h-10 px-4 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg text-sm transition-colors"
                data-testid="lookbook-new-section-btn">{saving ? 'Saving…' : 'Add section'}</button>
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-3">
            <button onClick={() => navigate(`/host-lookbook-preview?event_id=${encodeURIComponent(eventId)}`)}
              className="flex-1 h-11 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
              data-testid="lookbook-preview-btn">Preview</button>
            <button onClick={() => navigate(`/host-lookbook-launch?event_id=${encodeURIComponent(eventId)}`)} disabled={!canLaunch}
              className="flex-1 h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
              data-testid="lookbook-launch-btn">{isLive ? 'Republish' : 'Launch'}</button>
          </div>
          {!canLaunch && (
            <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-lg p-3 text-sm text-[#5B6472]" data-testid="lookbook-empty-hint">Place at least one selected view into a section before launching.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
